import { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import getAutoSuggession from "../../apis/getAutoSuggession";
import getAutoSuggessionForMainSearch from "../../apis/getAutoSuggessionForMainSearch";
import getSearchResultV2 from "../../apis/getSearchResultV2";
import { useAuthContext } from "../../context/AuthContext";
import ProfileImageMd from "../CommonComponent/ProfileImageMd";
import "./SearchFilters.css";
import user from "../../assets/images/no-photo.png";
import HorizontalLineChart from "../Charts/HorizontalLineChart";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapPin,
  faPhone,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import FirmLogo from "../Images/FirmLogo";
import SearchLoaderCard from "../Loaders/SearchLoaderCard";

const SearchFilters = () => {
  const { userDetails } = useAuthContext();
  const user_slug = JSON.parse(userDetails).user_slug;

  const autocompleteRef = useRef(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    keyword: "",
    jobRoles: [],
    jobRoleInput: "",
    skills: [],
    skillInput: "",
    certifications: [],
    certificationInput: "",
    languages: [],
    languageInput: "",
    location: [],
    locationInput: "",
    availability: "",
  });
  const [ownFirm, setOwnFirm] = useState(0);
  const [suggestions, setSuggestions] = useState({
    skills: [],
    languages: [],
    jobRoles: [],
    certifications: [],
    location: [],
  });

  const [mainSuggestions, setMainSuggestions] = useState([]);
  const debounceTimers = useRef({});

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        setMainSuggestions([]);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleAdvanced = () => {
    setShowAdvanced((prev) => {
      const newShowAdvanced = !prev;

      if (newShowAdvanced) {
        // Showing advanced search → reset main keyword and suggestions
        setFilters((prevFilters) => ({
          ...prevFilters,
          keyword: "",
        }));
        setMainSuggestions([]);
      } else {
        // Hiding advanced search → reset advanced filters only
        setFilters((prevFilters) => ({
          ...prevFilters,
          jobRoles: [],
          jobRoleInput: "",
          skills: [],
          skillInput: "",
          certifications: [],
          certificationInput: "",
          languages: [],
          languageInput: "",
          location: [],
          locationInput: "",
          availability: "",
        }));
      }

      return newShowAdvanced;
    });
  };

  const handleAdd = (type) => {
    const input = filters[type + "Input"].trim();
    if (input && !filters[type].includes(input)) {
      setFilters((prev) => ({
        ...prev,
        [type]: [...prev[type], input],
        [type + "Input"]: "",
      }));
    }
  };

  const handleRemove = (type, item) => {
    setFilters((prev) => ({
      ...prev,
      [type]: prev[type].filter((i) => i !== item),
    }));
  };

  const fetchSuggestions = async (type, keyword) => {
    try {
      const { data } = await getAutoSuggession(type, keyword);
      if (data?.status === 1) {
        setSuggestions((prev) => ({ ...prev, [type]: data.results || [] }));
      } else {
        setSuggestions((prev) => ({ ...prev, [type]: [] }));
      }
    } catch (error) {
      console.error(`Error fetching ${type} suggestions:`, error);
      setSuggestions((prev) => ({ ...prev, [type]: [] }));
    }
  };

  const fetchMainSuggestions = async (keyword) => {
    try {
      const { data } = await getAutoSuggessionForMainSearch(keyword);
      if (data?.status === 1) {
        setMainSuggestions(data.results || []);
      } else {
        setMainSuggestions([]);
      }
    } catch (error) {
      console.error("Error fetching main suggestions:", error);
      setMainSuggestions([]);
    }
  };

  const handleInputChange = (type, value) => {
    setFilters((prev) => ({ ...prev, [type + "Input"]: value }));

    clearTimeout(debounceTimers.current[type]);
    debounceTimers.current[type] = setTimeout(() => {
      if (value.trim().length >= 2) {
        fetchSuggestions(type, value.trim());
      } else {
        setSuggestions((prev) => ({ ...prev, [type]: [] }));
      }
    }, 300);
  };

  const handleMainInputChange = (value) => {
    setFilters((prev) => {
      let newFilters = {
        ...prev,
        keyword: value,
      };

      // ✅ Only reset advanced fields if advanced search is NOT active
      if (value.trim().length > 0 && !showAdvanced) {
        newFilters = {
          ...newFilters,
          jobRoles: [],
          jobRoleInput: "",
          skills: [],
          skillInput: "",
          certifications: [],
          certificationInput: "",
          languages: [],
          languageInput: "",
          location: [],
          locationInput: "",
          availability: "",
        };
      }

      return newFilters;
    });

    clearTimeout(debounceTimers.current.main);
    debounceTimers.current.main = setTimeout(() => {
      if (value.trim().length >= 2) {
        fetchMainSuggestions(value.trim());
      } else {
        setMainSuggestions([]);
      }
    }, 300);
  };

  const handleMainInputFocus = () => {
    if (showAdvanced) {
      setShowAdvanced(false);
      setFilters((prev) => ({
        ...prev,
        jobRoles: [],
        jobRoleInput: "",
        skills: [],
        skillInput: "",
        certifications: [],
        certificationInput: "",
        languages: [],
        languageInput: "",
        location: [],
        locationInput: "",
      }));
    }
  };

  const getSuggestions = (type) => {
    const input = filters[type + "Input"].toLowerCase();
    return suggestions[type]
      .filter(
        (item) =>
          item.name.toLowerCase().includes(input) &&
          !filters[type].includes(item.name)
      )
      .map((item) => (type === "location" ? item : { name: item.name }));
  };

  useEffect(() => {
    if (user_slug) {
      handleSearch();
    }
  }, [user_slug]);

  const handleSearch = async () => {
    const {
      keyword,
      jobRoles,
      skills,
      certifications,
      languages,
      location,
      availability,
    } = filters;

    const searchPayload = {
      user_slug,
      keyword: keyword.trim(),
      jobRoles,
      skills,
      certifications,
      languages,
      location,
      availability,
    };

    setLoading(true);
    setError(null);
    setSearchResults([]);

    try {
      const { data } = await getSearchResultV2(searchPayload);
      if (data?.status === 1 && data.result?.length > 0) {
        setSearchResults(data.result);
        await setOwnFirm(data?.own_firm);
      } else {
        setSearchResults([]);
        setError("No results found");
      }
    } catch (error) {
      setError("Error fetching search results");
      setSearchResults([]);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (label, type) => (
    <div className="col-md-4 mb-3" key={type}>
      <label className="form-label fw-semibold">{label}</label>
      <div className="position-relative">
        <input
          type="text"
          className="form-control"
          value={filters[type + "Input"]}
          onChange={(e) => handleInputChange(type, e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd(type)}
          placeholder={`Add ${label.toLowerCase()}`}
          autoComplete="off"
        />

        {filters[type + "Input"] && getSuggestions(type).length > 0 && (
          <div
            ref={autocompleteRef}
            className="main-suggestions-card position-absolute z-3 mt-2"
            style={{
              top: "100%",
              maxHeight: "260px",
              overflowY: "auto",
              width: "100%",
            }}
          >
            {getSuggestions(type).map((s) => (
              <div
                key={s.name + (s.type || "")}
                className="main-suggestion-item d-flex justify-content-between align-items-center px-3 py-2"
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    [type]: [...prev[type], s.name],
                    [type + "Input"]: "",
                  }))
                }
              >
                <div className="d-flex align-items-center gap-2">
                  <span className="suggestion-text">{s.name}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {filters[type].length > 0 && (
        <div className="selected-tags-container mt-2">
          {filters[type].map((item) => (
            <div
              key={item}
              className="badge badge-success d-flex align-items-center gap-2"
              tabIndex={0}
              aria-label={`${item} selected. Press delete or backspace to remove.`}
              onKeyDown={(e) => {
                if (e.key === "Delete" || e.key === "Backspace") {
                  handleRemove(type, item);
                }
              }}
            >
              {item}
              <button
                type="button"
                className="btn-remove"
                onClick={() => handleRemove(type, item)}
                aria-label={`Remove ${item}`}
                title="Remove"
              >
                <i className="bi bi-x-circle text-success"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const getTypeIcon = (type) => {
    switch ((type || "").toLowerCase()) {
      case "skill":
        return "bi-lightning-charge";
      case "job role":
        return "bi-briefcase";
      case "certification":
        return "bi-patch-check";
      case "language":
        return "bi-translate";
      case "location":
        return "bi-geo-alt";
      default:
        return "bi-search";
    }
  };

  const getBadgeBgColor = (type) => {
    switch (type?.toLowerCase()) {
      case "skill":
        return "#0dcaf0"; // cyan
      case "jobrole":
        return "#198754"; // green
      case "certification":
        return "#ffc107"; // yellow
      case "language":
        return "#6f42c1"; // purple
      case "location":
        return "#fd7e14"; // orange
      default:
        return "#6c757d"; // gray
    }
  };

  const matchedFieldNamesMap = {
    user_profile_role: "Job Role",
    profile_bio: "Profile Bio",
    city_name: "City",
    country_name: "Country",
    state_name: "State",
    skill_name: "Skills",
    degrees: "Degrees",
    subjects: "Subjects",
    schools: "Schools",
    employer_names: "Employers",
    job_titles: "Job Titles",
    languages: "Languages",
  };

  const displayProfileName = (item) => {
    if (ownFirm !== "" && ownFirm !== undefined) {
      if (ownFirm === item.firm_id) {
        return (
          <div className="d-block">
            <span className="text-dark text-md fw-semibold">
              {item.resource_name}
            </span>
          </div>
        );
      }
    }
  };

  const displayProfileRole = (item) => {
    return (
      <>
        {item.user_profile_role !== "" && (
          <div className="d-block">
            <span className="text-primary">{item.user_profile_role}</span>
          </div>
        )}
      </>
    );
  };

  const displayProfilePicture = (item) => {
    if (ownFirm !== "" && ownFirm !== undefined) {
      if (ownFirm === item.firm_id) {
        return <ProfileImageMd imgSrc={item.profile_image_path} />;
      } else {
        return (
          <div className="profile-image-md">
            <img className="img-fluid" src={user} alt="" />
          </div>
        );
      }
    } else {
      return (
        <div className="profile-image-md">
          <img className="img-fluid" src={user} alt="" />
        </div>
      );
    }
  };

  const checkContactBlockDisplay = (item) => {
    if (ownFirm !== "" && ownFirm !== undefined) {
      if (ownFirm === item.firm_id) {
        return (
          <div className="d-flex flex-wrap gap-2 text-sm mt-sm-2">
            {displayEmail(item)}
            {displayPhone(item)}
            {displayLocation(item)}
          </div>
        );
      } else {
        return (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {displayLocation(item)}
          </div>
        );
      }
    }
  };

  const displayEmail = (item) => {
    if (
      ownFirm !== "" &&
      ownFirm !== undefined &&
      ownFirm === item.firm_id &&
      item.user_email &&
      item.user_email.trim() !== ""
    ) {
      return (
        <div className="d-flex me-3 align-items-center">
          <span className="text-muted me-2">
            <FontAwesomeIcon icon={faEnvelope} />
          </span>
          <span className="text-muted">{item.user_email}</span>
        </div>
      );
    }

    return null; // Nothing to display
  };

  const displayPhone = (item) => {
    if (
      ownFirm !== "" &&
      ownFirm !== undefined &&
      ownFirm === item.firm_id &&
      item.user_phone &&
      item.user_phone.trim() !== ""
    ) {
      return (
        <div className="d-flex me-3 align-items-center">
          <span className="text-muted me-2">
            <FontAwesomeIcon icon={faPhone} />
          </span>
          <span className="text-muted">{item.user_phone}</span>
        </div>
      );
    }

    return null; // Do not display anything if conditions are not met
  };

  const displayLocation = (item) => {
    const locationParts = [];

    if (item.city_name) locationParts.push(item.city_name);
    if (item.state_name) locationParts.push(item.state_name);
    if (item.country_name) locationParts.push(item.country_name);

    if (locationParts.length === 0) return null; // Nothing to display

    const locationText = locationParts.join(", ");

    return (
      <div className="d-flex align-items-center">
        <span className="text-muted me-2">
          <FontAwesomeIcon icon={faMapPin} />
        </span>
        <span className="text-muted">{locationText}</span>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white p-3 rounded position-relative shadow-sm">
        <h2>What are you looking for?</h2>

        <div className="row my-2 align-items-center">
          <div className="col-md-9 position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Keywords"
              value={filters.keyword}
              onChange={(e) => handleMainInputChange(e.target.value)}
              onFocus={handleMainInputFocus}
              disabled={showAdvanced}
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setMainSuggestions([]);
                  handleSearch();
                }
              }}
            />
            {filters.keyword && mainSuggestions.length > 0 && !showAdvanced && (
              <div
                ref={autocompleteRef}
                className="main-suggestions-card position-absolute z-3 mt-1"
                style={{
                  top: "100%",
                  maxHeight: "260px",
                  overflowY: "auto",
                  width: "97.5%",
                  minWidth: "300px",
                }}
              >
                {mainSuggestions.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    className="main-suggestion-item d-flex justify-content-between align-items-center px-3 py-2"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        keyword: item.name || item.title || "",
                      }));
                      setMainSuggestions([]);
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className={`bi ${getTypeIcon(item.type)} icon-bg`}></i>
                      <span className="suggestion-text">
                        {item.name || item.title || ""}
                      </span>
                    </div>
                    {item.type && (
                      <span
                        className="badge text-capitalize"
                        style={{
                          backgroundColor: getBadgeBgColor(item.type),
                          color: "white",
                        }}
                      >
                        {item.type}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="col-md-3 d-grid mt-2 mt-md-0">
            <button
              className="btn btn-primary btn-sm rounded"
              onClick={handleSearch}
              style={{ height: "calc(2em + 1rem + 2px)" }}
            >
              Search
            </button>
          </div>
        </div>

        <div className="d-block" aria-expanded={showAdvanced}>
          <span
            onClick={toggleAdvanced}
            className="align-self-start cursor-pointer text-decoration-none text-primary fw-semibold"
          >
            {showAdvanced ? "Hide Advanced Search ▲" : "Advanced Search ▼"}
          </span>
        </div>

        {showAdvanced && (
          <div className="row mt-2">
            {renderInput("Job Roles", "jobRoles")}
            {renderInput("Skills", "skills")}
            {renderInput("Certifications", "certifications")}
            {renderInput("Languages", "languages")}
            {renderInput("Location", "location")}

            {/* New Availability Dropdown */}
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Availability</label>
              <select
                className="form-select"
                value={filters.availability}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    availability: e.target.value,
                  }))
                }
              >
                <option value="99999">Select availability</option>
                <option value="40">40 hrs/week</option>
                <option value="30">30 hrs/week</option>
                <option value="20">20 hrs/week</option>
                <option value="10">10 hrs/week</option>
                <option value="0">N/A</option>
              </select>
            </div>
          </div>
        )}
      </div>
      {/* Search Results */}
      <div className="mt-4">
        {loading && <SearchLoaderCard />}

        {error !== null && !loading && (
          <div className="alert alert-warning" role="alert">
            {error}
          </div>
        )}

        {!loading && searchResults.length > 0 && (
          <div className="d-flex flex-column gap-3">
            {searchResults.map((item, idx) => (
              <Link to={"resources/details/" + item.user_slug}>
                <div key={item.user_slug || idx} className="card">
                  <div className="card-body">
                    <div className="row align-items-center">
                      {/* Profile Image + Name + Role */}
                      <div className="col-12 col-lg-8 d-flex mb-2 mb-lg-0">
                        <div className="me-3">
                          {item.profile_image_path ? (
                            <>{displayProfilePicture(item)}</>
                          ) : (
                            <div
                              style={{
                                width: "50px",
                                height: "50px",
                                borderRadius: "50%",
                                backgroundColor: "#ccc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "1.25rem",
                                userSelect: "none",
                              }}
                            >
                              {item.resource_name?.charAt(0) || "?"}
                            </div>
                          )}
                        </div>
                        <div className="justify-content-center">
                          {displayProfileName(item)}
                          {displayProfileRole(item)}
                        </div>
                      </div>

                      {/* Firm logo + chart */}
                      <div className="col-12 col-lg-4 d-flex justify-content-between align-items-center">
                        <div>
                          <FirmLogo url={item.firm_logo} />
                        </div>
                        <div
                          className="text-sm text-dark"
                          style={{ marginTop: "-6px" }}
                        >
                          <span>{item.availability || 0} hrs / Week</span>
                          <HorizontalLineChart
                            value={item.availability || 0}
                            width={100}
                            thickness={15}
                          />
                        </div>
                      </div>
                    </div>
                    {checkContactBlockDisplay(item)}

                    {/* Skills Section */}
                    {item.skill_name &&
                      item.skill_name.trim() !== "" &&
                      (() => {
                        const skills = item.skill_name
                          .split(",")
                          .map((skill) => skill.trim())
                          .filter((skill) => skill !== "");

                        const visibleSkills = skills.slice(0, 4);
                        const remainingCount =
                          skills.length - visibleSkills.length;

                        return (
                          <>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {visibleSkills.map((skill, idx) => (
                                <span
                                  key={idx}
                                  className="badge badge-success text-sm fw-regular"
                                  title={skill}
                                >
                                  {skill}
                                </span>
                              ))}

                              {remainingCount > 0 && (
                                <span
                                  className="badge badge-dark text-sm fw-regular"
                                  title={`${remainingCount} more skills`}
                                >
                                  +{remainingCount} more
                                </span>
                              )}
                            </div>
                          </>
                        );
                      })()}

                    {(item.matched_fields?.length > 0 ||
                      item.match_score > 0 ||
                      item.matched_skills?.length > 0 ||
                      item.unmatched_skills?.length > 0) && (
                      <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
                        {/* Matched Fields */}
                        {item.matched_fields?.length > 0 && (
                          <div className="d-flex align-items-center flex-wrap gap-2">
                            <span className="fw-semibold text-sm text-muted">
                              Matched Fields:
                            </span>
                            {item.matched_fields.map((field, i) => {
                              const displayName =
                                matchedFieldNamesMap[field] || field;
                              return (
                                <span
                                  key={`matched-field-${i}`}
                                  className="badge badge-warning"
                                  style={{
                                    fontSize: "0.85em",
                                    whiteSpace: "nowrap",
                                    cursor: "default",
                                  }}
                                  title={`Matched text: ${displayName}`}
                                >
                                  {displayName}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {/* Matching Score */}
                        {item.match_score > 0 && (
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-semibold text-sm text-muted">
                              Score:
                            </span>
                            <span
                              className="badge badge-primary"
                              style={{
                                fontSize: "0.85em",
                                whiteSpace: "nowrap",
                                cursor: "default",
                              }}
                            >
                              {item.match_score}%
                            </span>
                          </div>
                        )}

                        {/* Matched Skills */}
                        {item.matched_skills?.length > 0 && (
                          <div className="d-flex align-items-center flex-wrap gap-2">
                            <span className="fw-semibold text-sm text-muted">
                              Matched Skills:
                            </span>
                            {item.matched_skills.map((field, i) => {
                              const displayName =
                                matchedFieldNamesMap[field] || field;
                              return (
                                <span
                                  key={`matched-skill-${i}`}
                                  className="badge badge-success"
                                  style={{
                                    fontSize: "0.85em",
                                    whiteSpace: "nowrap",
                                    cursor: "default",
                                  }}
                                  title={`Matched text: ${displayName}`}
                                >
                                  {displayName}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {/* Unmatched Skills */}
                        {item.unmatched_skills?.length > 0 && (
                          <div className="d-flex align-items-center flex-wrap gap-2">
                            <span className="fw-semibold text-sm">
                              Not Found Skills:
                            </span>
                            {item.unmatched_skills.map((field, i) => {
                              const displayName =
                                matchedFieldNamesMap[field] || field;
                              return (
                                <span
                                  key={`unmatched-skill-${i}`}
                                  className="badge bg-danger"
                                  style={{
                                    fontSize: "0.85em",
                                    whiteSpace: "nowrap",
                                    cursor: "default",
                                  }}
                                  title={`Matched text: ${displayName}`}
                                >
                                  {displayName}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchFilters;
